import React, { useState, useMemo } from "react";
import Grid from "@mui/material/Grid2"; // שימוש ב-Grid v2
import { Box, Typography, Paper, Chip, IconButton, Collapse, Button, Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Product } from "../../../../interfaces/DTO/mislaka_xml_models";
import { colorInsuranceType, colorPayoutMethod } from "../../../../utils/colorEnumFormatter";
import { formatInsuranceType, formatPayoutMethod, formatYesNo, formatOccupationType, formatUnderwritingType, formatWaitingPeriod, formatQualificationPeriod, formatDisabilityRisk, formatInsuranceSumRate, formatFixedOrVariableSum, formatCoveragePayerType, formatDiscountTerms, formatDiscountType, formatCoverageTypeAtManufacturer } from "../../../../utils/pensionEnumFormatter";
import { formatCurrency, formatIsoDate } from "../../../../utils/namesFormater";
import { BeneficiaryInfoCard } from "./BeneficiaryInfoCard";
import { OccupationInfoCard } from "./OccupationInfoCard";
import { AdditionalDetailsCard } from "./AdditionalDetailsCard";
import { PensionDiscountInfoCard } from "./PensionDiscountInfoCard";
import { CoverageAmountsStackedChart } from "../Charts/CoverageAmountsStackedChart";
import { InsuranceCoverageInfo, InsuranceType, InsuredType } from "../../../../interfaces/DTO/mislaka_xml_insurence_models";


interface InsuranceCoverageMetadataProps {

  product: Product;
}

const InsuranceCoverageMetadata: React.FC<InsuranceCoverageMetadataProps> = ({ product }) => {
    const policy = product.policies_or_accounts[0]; // Assuming we are displaying the first policy
    const coverageCount = policy.insurence_coverages?.length || 0;
    const coveragesMap: Record<string, InsuranceCoverageInfo[]> = {};
    const [expandedCoverages, setExpandedCoverages] = useState<Record<number, boolean>>({});
    const [chartDialogOpen, setChartDialogOpen] = useState(false);
    const [selectedCoverageMap, setSelectedCoverageMap] = useState<Record<string, InsuranceCoverageInfo[]>>({});
    const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
    const [selectedDiscountInfo, setSelectedDiscountInfo] = useState<any[]>([]);
    const [selectedInsuredId, setSelectedInsuredId] = useState<string | null>(null);

    // זיהוי מבוטח ראשי ומבוטחים אחרים
    const insuredTypes = useMemo(() => {
      const clientId = product.client?.client_id;
      const otherInsured = new Map<string, string>(); // id_number -> display name

      // בדיקה אם הקליינט מופיע כמבוטח משני באחד הכיסויים
      let clientIsSecondary = false;
      policy.insurence_coverages?.forEach((coverage) => {
        coverage.insured_details?.forEach((detail) => {
          if (detail.id_number === clientId && coverage.coverage_details?.insured_type === InsuredType.SECONDARY) {
            clientIsSecondary = true;
          }
          if (detail.id_number && detail.id_number !== clientId) {
            otherInsured.set(detail.id_number, `מבוטח ${detail.id_number}`);
          }
        });
      });

      return {
        primaryInsuredId: clientId,
        clientIsSecondary,
        otherInsured: Array.from(otherInsured.entries()).map(([id, name]) => ({ id, name }))
      };
    }, [policy.insurence_coverages, product.client?.client_id]);

    // סינון כיסויים לפי מבוטח נבחר
    const filteredCoverages = useMemo(() => {
      if (!selectedInsuredId) {
        // אם לא נבחר מבוטח ספציפי, הצג את כל כיסויי המבוטח הראשי
        return policy.insurence_coverages?.filter(coverage => {
          // הצג כיסוי אם אין insured_details (כיסוי ראשי) או אם יש את המבוטח הראשי
          if (!coverage.insured_details || coverage.insured_details.length === 0) return true;
          return coverage.insured_details.some(detail => detail.id_number === insuredTypes.primaryInsuredId);
        }) || [];
      } else {
        // הצג כיסויים של המבוטח הנבחר
        return policy.insurence_coverages?.filter(coverage => {
          return coverage.insured_details?.some(detail => detail.id_number === selectedInsuredId);
        }) || [];
      }
    }, [policy.insurence_coverages, selectedInsuredId, insuredTypes.primaryInsuredId]);

    // חישוב מספר כיסויים לכל מבוטח
    const primaryInsuredCoverageCount = useMemo(() => {
      return policy.insurence_coverages?.filter(coverage => {
        if (!coverage.insured_details || coverage.insured_details.length === 0) return true;
        return coverage.insured_details.some(detail => detail.id_number === insuredTypes.primaryInsuredId);
      }).length || 0;
    }, [policy.insurence_coverages, insuredTypes.primaryInsuredId]);

    const otherInsuredCoverageCounts = useMemo(() => {
      const counts = new Map<string, number>();
      insuredTypes.otherInsured.forEach(({ id }) => {
        const count = policy.insurence_coverages?.filter(coverage => {
          return coverage.insured_details?.some(detail => detail.id_number === id);
        }).length || 0;
        counts.set(id, count);
      });
      return counts;
    }, [policy.insurence_coverages, insuredTypes.otherInsured]);

    policy.insurence_coverages?.forEach((coverage) => {
      const name = coverage.coverage_name || `כיסוי ${coverage.coverage_number}`;
      if (coverage.insurance_coverage?.length) {
        coveragesMap[name] = coverage.insurance_coverage;
      }
    });
    if (coverageCount === 0) {
      return <Typography color="error">לא נמצאו כיסויים ביטוחיים</Typography>;
    }

    const toggleExpanded = (index: number) => {
      setExpandedCoverages(prev => ({
        ...prev,
        [index]: !prev[index]
      }));
    };

    const handleInsuredChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
      setSelectedInsuredId(newValue);
    };

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">כיסויים ביטוחיים ({coverageCount})</Typography>

          {/* כפתורי בחירת מבוטח - יוצגו רק אם יש מבוטח אחר */}
          {insuredTypes.otherInsured.length > 0 && (
            <ToggleButtonGroup
              value={selectedInsuredId}
              exclusive
              onChange={handleInsuredChange}
              size="small"
              sx={{ direction: 'rtl' }}
            >
              <ToggleButton value={null}>
                {insuredTypes.clientIsSecondary ? 'מבוטח משני' : 'מבוטח ראשי'}
                {product.client?.client_name ? ` – ${product.client.client_name}` : insuredTypes.primaryInsuredId ? ` – ${insuredTypes.primaryInsuredId}` : ''}
                {` (${primaryInsuredCoverageCount})`}
              </ToggleButton>
              {insuredTypes.otherInsured.map(({ id, name }) => (
                <ToggleButton key={id} value={id}>
                  {name} ({otherInsuredCoverageCounts.get(id) || 0})
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>

        {filteredCoverages.map((coverage, index) => {
          // הסכום כבר תוקן בנרמול, אז פשוט לוקחים אותו
          const displayAmount = coverage.coverage_details?.insurance_sum || 0;

          // בדיקת תוקף הכיסוי
          const paymentStopDate = coverage.coverage_details?.insurance_fees?.payment_stop_date;
          const updateDate = policy.update_date;
          const isExpired = paymentStopDate && updateDate && new Date(paymentStopDate) < new Date(updateDate);

          return (
          <Paper key={index} elevation={2} sx={{ p: 2, backgroundColor: "#f5f5f5", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>כיסוי {index + 1}</Typography>

            {/* הודעת פג תוקף */}
            {isExpired && (
              <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#fff3cd", borderRadius: 1, border: "1px solid #ffc107" }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#856404" }}>
                  ⚠️ כיסוי זה פג תוקף - תשלומים הופסקו ב-{formatIsoDate(paymentStopDate)}
                </Typography>
              </Box>
            )}

            <Grid container spacing={2}>
              {/* שם כיסוי */}
              <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">שם כיסוי:</Typography></Grid>
              <Grid size={{ xs: 6, md: 8 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* אימוג'י מטריה אם סכום כיסוי = 0 וסוג ביטוח לא מוות או משולבת חיסכון */}
                  {/* אבל לא אם יש כיסוי ביטוחי בסיסי בביטוח מנהלים */}
                  {displayAmount === 0 &&
                   coverage.coverage_details?.insurance_kind !== InsuranceType.DEATH &&
                   coverage.coverage_details?.insurance_kind !== InsuranceType.COMBINED_SAVINGS_PLAN && (
                    <Typography sx={{ fontSize: 20 }}>☂️</Typography>
                  )}
                  <Typography sx={{ fontWeight: 700 }}>{coverage.coverage_name || "-"}</Typography>
                </Box>
              </Grid>

              {/* מספר פוליסה נוסף - יוצג רק אם יש ערך */}
              {coverage.other_policy_number && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר פוליסה נוסף:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}><Typography>{coverage.other_policy_number}</Typography></Grid>
                </>
              )}

              {/* סכום כיסוי */}
              <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סכום כיסוי:</Typography></Grid>
              <Grid size={{ xs: 6, md: 8 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {formatCurrency(displayAmount)}
                </Typography>
              </Grid>

              {/* סוג ביטוח - יוצג כ-CHIP */}
              {coverage.coverage_details?.insurance_kind && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סוג ביטוח:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}>
                    <Chip
                      label={formatInsuranceType(coverage.coverage_details.insurance_kind)}
                      style={{ backgroundColor: colorInsuranceType(coverage.coverage_details.insurance_kind), color: "#fff" }}
                    />
                  </Grid>
                </>
              )}
  
              {/* שיטת תשלום סכום ביטוח - יוצג כ-CHIP */}
                 {coverage.coverage_details?.payout_method && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">שיטת תשלום סכום ביטוח:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}>
                    <Chip 
                      label={formatPayoutMethod(coverage.coverage_details.payout_method)} 
                      style={{ backgroundColor: colorPayoutMethod(coverage.coverage_details.payout_method), color: "#fff" }}
                    />
                  </Grid>
                </>
              )}
  
              {/* פרטי מבוטח */}
              {coverage.insured_details && coverage.insured_details.length > 0 && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">פרטי מבוטח:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}>
                    <Typography>
                       {coverage.insured_details.map((detail) => {
                        const isClient = detail.id_number === product.client?.client_id;

                        return `${detail.id_number}${isClient ? " (מבוטח ראשי)" : " (מבוטח אחר)"}`;
                      }).join(", ")}
                    </Typography>
                  </Grid>
                </>
              )}

              {/* עלויות ביטוח - רק אם יש ערכים שאינם 0 או null */}
              {coverage.coverage_details?.insurance_fees && (() => {
                const fees = coverage.coverage_details.insurance_fees;
                const hasFees = (fees.fee != null && fees.fee !== 0) ||
                                (fees.discount_value != null && fees.discount_value !== 0) ||
                                (fees.insurance_percentage != null && fees.insurance_percentage !== 0) ||
                                (fees.salary_percentage != null && fees.salary_percentage !== 0);

                if (!hasFees) return null;

                return (
                  <>
                    <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">עלויות:</Typography></Grid>
                    <Grid size={{ xs: 6, md: 8 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {fees.fee != null && fees.fee !== 0 && (
                          <Typography variant="body2">
                            <strong>עלות:</strong> {formatCurrency(fees.fee)}
                          </Typography>
                        )}
                        {fees.discount_value != null && fees.discount_value !== 0 && (
                          <Typography variant="body2">
                            <strong>הנחה:</strong> {formatCurrency(fees.discount_value)}
                          </Typography>
                        )}
                        {fees.insurance_percentage != null && fees.insurance_percentage !== 0 && (
                          <Typography variant="body2">
                            <strong>אחוז ביטוח:</strong> {fees.insurance_percentage}%
                          </Typography>
                        )}
                        {fees.salary_percentage != null && fees.salary_percentage !== 0 && (
                          <Typography variant="body2">
                            <strong>
                              {coverage.coverage_details?.insurance_kind === InsuranceType.DEATH || coverage.coverage_details?.insurance_kind === InsuranceType.ACCIDENTAL_DEATH
                                ? "מספר משכורות:"
                                : coverage.coverage_details?.insurance_kind === InsuranceType.PREMIUM_WAIVER
                                ? "שחרור מתשלום פרמיות:"
                                : "אחוז משכר:"}
                            </strong>{" "}
                            {coverage.coverage_details?.insurance_kind === InsuranceType.DEATH || coverage.coverage_details?.insurance_kind === InsuranceType.ACCIDENTAL_DEATH
                              ? `${fees.salary_percentage} משכורות`
                              : coverage.coverage_details?.insurance_kind === InsuranceType.PREMIUM_WAIVER
                              ? "0%"
                              : `${(fees.salary_percentage * 100).toFixed(0)}%`}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </>
                );
              })()}

              {/* הצגת מספר האובייקטים בכל קבוצה - רק אם יש נתונים */}
              {coverage.coverages?.length > 0 && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר כיסויים:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}><Typography>{coverage.coverages.length}</Typography></Grid>
                </>
              )}
  
              {coverage.beneficiaries?.length > 0 && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר מוטבים:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}><Typography>{coverage.beneficiaries.length}</Typography></Grid>
                </>
              )}

              {/* כפתור עוד פרטים */}
              <Grid size={12}>
                <Box
                  onClick={() => toggleExpanded(index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    mt: 1,
                    '&:hover': {
                      opacity: 0.7
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "primary.main" }}>
                    עוד פרטים
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{
                      transform: expandedCoverages[index] ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                      p: 0
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>

                {/* פרטים נוספים בתוך Collapse */}
                <Collapse in={expandedCoverages[index]} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#ffffff", borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>פרטים נוספים</Typography>
                    <Grid container spacing={2}>

                      {/* מספר כיסוי */}
                      {coverage.coverage_number && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר כיסוי:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{coverage.coverage_number}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* סוג כיסוי אצל יצרן */}
                      {coverage.coverage_type_at_manufacturer !== undefined && coverage.coverage_type_at_manufacturer !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סוג כיסוי אצל יצרן:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatCoverageTypeAtManufacturer(coverage.coverage_type_at_manufacturer)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* קוד נספח כיסוי */}
                      {coverage.coverage_details?.coverage_attachment_code !== undefined && coverage.coverage_details.coverage_attachment_code !== "" && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">קוד נספח כיסוי:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{coverage.coverage_details.coverage_attachment_code}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* מספר תקופות פרמיה */}
                      {coverage.premium_period?.length > 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר תקופות פרמיה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{coverage.premium_period.length}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* כולל סיעוד וכולל פרנציזה */}
                      {(coverage.coverage_details?.includes_nursing && coverage.coverage_details.includes_nursing !== 0) ||
                       (coverage.coverage_details?.includes_prenzisa && coverage.coverage_details.includes_prenzisa !== 0) ? (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">כולל סיעוד / פרנציזה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>
                              {formatYesNo(coverage.coverage_details?.includes_nursing)} / {formatYesNo(coverage.coverage_details?.includes_prenzisa)}
                            </Typography>
                          </Grid>
                        </>
                      ) : null}

                      {/* סוג עיסוק */}
                      {coverage.coverage_details?.occupation_type !== undefined && coverage.coverage_details.occupation_type !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סוג עיסוק:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatOccupationType(coverage.coverage_details.occupation_type)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* חתום */}
                      {coverage.coverage_details?.signed !== undefined && coverage.coverage_details.signed !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">חתום:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatYesNo(coverage.coverage_details.signed)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תאריך חתימה */}
                      {coverage.coverage_details?.signing_date && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תאריך חתימה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatIsoDate(coverage.coverage_details.signing_date)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תאריך תחילה */}
                      {coverage.coverage_details?.start_date && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תאריך תחילה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatIsoDate(coverage.coverage_details.start_date)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תאריך סיום */}
                      {coverage.coverage_details?.end_date && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תאריך סיום:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatIsoDate(coverage.coverage_details.end_date)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* אחוז הגדלת גג חתם לאכ"ע */}
                      {coverage.coverage_details?.cap_increase_percentage !== undefined && coverage.coverage_details.cap_increase_percentage !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">אחוז הגדלת גג חתם לאכ"ע:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{coverage.coverage_details.cap_increase_percentage}%</Typography>
                          </Grid>
                        </>
                      )}

                      {/* החרגה */}
                      {coverage.coverage_details?.underwriting !== undefined && coverage.coverage_details.underwriting !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">החרגה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatYesNo(coverage.coverage_details.underwriting)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* סוג החרגה */}
                      {coverage.coverage_details?.underwriting_type !== undefined && coverage.coverage_details.underwriting_type !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סוג החרגה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatUnderwritingType(coverage.coverage_details.underwriting_type)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תקופת המתנה */}
                      {coverage.coverage_details?.waiting_period !== undefined && coverage.coverage_details.waiting_period !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תקופת המתנה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatYesNo(coverage.coverage_details.waiting_period)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תקופת המתנה בחודשים */}
                      {coverage.coverage_details?.waiting_period_months !== undefined && coverage.coverage_details.waiting_period_months !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">משך תקופת המתנה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{coverage.coverage_details.waiting_period_months} חודשים</Typography>
                          </Grid>
                        </>
                      )}

                      {/* קצבה צמודה למדד */}
                      {coverage.coverage_details?.waiting_period !== undefined && coverage.coverage_details.waiting_period !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">קצבה צמודה למדד:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatWaitingPeriod(coverage.coverage_details.waiting_period)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תקופת הכשרה */}
                      {coverage.coverage_details?.qualification_period !== undefined && coverage.coverage_details.qualification_period !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תקופת הכשרה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatQualificationPeriod(coverage.coverage_details.qualification_period)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* נכות מתפתחת */}
                      {coverage.coverage_details?.disability_risk !== undefined && (coverage.coverage_details.disability_risk === 1 || coverage.coverage_details.disability_risk === 2) && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">נכות מתפתחת:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatDisabilityRisk(coverage.coverage_details.disability_risk)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* ביטול קיזוז ממשלתי */}
                      {coverage.coverage_details?.government_deduction_cancellation !== undefined && coverage.coverage_details.government_deduction_cancellation !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">ביטול קיזוז ממשלתי:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatYesNo(coverage.coverage_details.government_deduction_cancellation)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* סכום/שיעור ביטוח */}
                      {coverage.coverage_details?.insurance_fees?.insurance_sum_rate !== undefined && (coverage.coverage_details.insurance_fees.insurance_sum_rate === 1 || coverage.coverage_details.insurance_fees.insurance_sum_rate === 2) && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סכום/שיעור ביטוח:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatInsuranceSumRate(coverage.coverage_details.insurance_fees.insurance_sum_rate)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* סכום כיסוי ביטוחי קבוע/משתנה */}
                      {coverage.coverage_details?.insurance_fees?.fixed_or_variable_sum !== undefined && (coverage.coverage_details.insurance_fees.fixed_or_variable_sum === 1 || coverage.coverage_details.insurance_fees.fixed_or_variable_sum === 2) && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סכום כיסוי ביטוחי קבוע/משתנה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography>{formatFixedOrVariableSum(coverage.coverage_details.insurance_fees.fixed_or_variable_sum)}</Typography>
                              {coverage.coverage_details.insurance_fees.fixed_or_variable_sum === 2 && coverage.insurance_coverage && coverage.insurance_coverage.length > 0 && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    const coverageName = coverage.coverage_name || `כיסוי ${coverage.coverage_number}`;
                                    setSelectedCoverageMap({ [coverageName]: coverage.insurance_coverage });
                                    setChartDialogOpen(true);
                                  }}
                                >
                                  הצג
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* שיעור ביטוח קבוע/משתנה */}
                      {coverage.coverage_details?.insurance_fees?.fixed_or_variable_rate !== undefined && (coverage.coverage_details.insurance_fees.fixed_or_variable_rate === 1 || coverage.coverage_details.insurance_fees.fixed_or_variable_rate === 2) && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">שיעור ביטוח קבוע/משתנה:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatFixedOrVariableSum(coverage.coverage_details.insurance_fees.fixed_or_variable_rate)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* משלם הכיסוי */}
                      {coverage.coverage_details?.coverage_payer !== undefined && coverage.coverage_details.coverage_payer !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">משלם הכיסוי:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatCoveragePayerType(coverage.coverage_details.coverage_payer)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תנאי הנחה לביטוח */}
                      {coverage.coverage_details?.insurance_fees?.discount_terms !== undefined && coverage.coverage_details.insurance_fees.discount_terms !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תנאי הנחה לביטוח:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography>{formatDiscountTerms(coverage.coverage_details.insurance_fees.discount_terms)}</Typography>
                              {coverage.coverage_details.insurance_fees.discount === 1 && coverage.insurance_discount && coverage.insurance_discount.length > 0 && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setSelectedDiscountInfo(coverage.insurance_discount);
                                    setDiscountDialogOpen(true);
                                  }}
                                >
                                  הצג
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        </>
                      )}

                      {/* סוג הנחה לכיסוי */}
                      {coverage.coverage_details?.insurance_fees?.insurance_discount_type !== undefined && coverage.coverage_details.insurance_fees.insurance_discount_type !== 0 && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">סוג הנחה לכיסוי:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatDiscountType(coverage.coverage_details.insurance_fees.insurance_discount_type)}</Typography>
                          </Grid>
                        </>
                      )}

                      {/* תאריך הפסקת תשלום */}
                      {coverage.coverage_details?.insurance_fees?.payment_stop_date && (
                        <>
                          <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">תאריך הפסקת תשלום:</Typography></Grid>
                          <Grid size={{ xs: 6, md: 8 }}>
                            <Typography>{formatIsoDate(coverage.coverage_details.insurance_fees.payment_stop_date)}</Typography>
                          </Grid>
                        </>
                      )}

                    </Grid>
                  </Box>
                </Collapse>
              </Grid>

              {coverage.insurance_discount?.length > 0 && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר הנחות ביטוח:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}><Typography>{coverage.insurance_discount.length}</Typography></Grid>
                </>
              )}
  
              {coverage.insurance_coverage?.length > 0 && (
                <>
                  <Grid size={{ xs: 6, md: 4 }}><Typography variant="body2">מספר כיסויים ביטוחיים:</Typography></Grid>
                  <Grid size={{ xs: 6, md: 8 }}><Typography>{coverage.insurance_coverage.length}</Typography></Grid>
                </>
              )}
              {coverage.occupation_info?.length > 0 && (
                <>
                  {coverage.occupation_info.map((occupation, idx) => (
                    <OccupationInfoCard
                      key={idx}
                      occupation={occupation}
                    />
                  ))}
                </>
              )}
              {coverage.additional_details?.length > 0 && (
                <>
                  {coverage.additional_details.map((detail, idx) => (
                    <AdditionalDetailsCard
                      key={idx}
                      detail={detail}
                    />
                  ))}
                </>
              )}
               {coverage.beneficiaries?.length > 0 && (
                <>
                  {coverage.beneficiaries.map((beneficiary, idx) => (
                    <BeneficiaryInfoCard
                      key={idx}
                      beneficiary={beneficiary}
                    />
                  ))}
                </>
              )}
            </Grid>
          </Paper>
          );
        })}

        {/* גרף משולב של כל הכיסויים */}
        {Object.keys(coveragesMap).length > 0 && (
          <CoverageAmountsStackedChart coveragesMap={coveragesMap} />
        )}

        {/* דיאלוג להצגת גרף השתנות כיסוי ספציפי */}
        <Dialog
          open={chartDialogOpen}
          onClose={() => setChartDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          dir="rtl"
        >
       
          <DialogContent>
            <CoverageAmountsStackedChart coveragesMap={selectedCoverageMap} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChartDialogOpen(false)}>סגור</Button>
          </DialogActions>
        </Dialog>

        {/* דיאלוג להצגת מידע הנחות */}
        <Dialog
          open={discountDialogOpen}
          onClose={() => setDiscountDialogOpen(false)}
          maxWidth="md"
          fullWidth
          dir="rtl"
        >
          <DialogTitle>מידע הנחות פנסיוניות</DialogTitle>
          <DialogContent>
            <PensionDiscountInfoCard discountInfo={selectedDiscountInfo} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDiscountDialogOpen(false)}>סגור</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  export default InsuranceCoverageMetadata;